import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, ConfirmDialog, ErrorState, IconButton, Input, PageHeader, PageLoader, Switch, Textarea, useToast } from '../../../components/ui';
import { useCrumb } from '../../../components/layout/Breadcrumbs/Breadcrumbs';
import { ItemPicker } from '../../../components/common/Pickers/Pickers';
import { FormSection, TableSection } from '../../../components/common/Parts/Parts';
import { useAction } from '../../../hooks/useAction';
import { useFetch } from '../../../hooks/useFetch';
import { cn } from '../../../utils/cn';
import { serverFieldErrors, trimOrUndef } from '../../../utils/validation';
import { useForm } from '../../../hooks/useForm';
import { templateService } from '../services/templateService';
import type { SupplyBy } from '../../quotations/types/quotationTypes';
import type { FormEvent } from 'react';
import type { FormErrors } from '../../../hooks/useForm';
import type { TemplateInput } from '../types/templateTypes';

/** One editable template line (no rates: those come from Materials and Work rates). */
interface TemplateLine {
  key: string;
  itemId: string | null;
  itemLabel: string;
  description: string;
  qty: string;
  supplyBy: SupplyBy;
}

let seq = 0;
const row = (extra: Partial<TemplateLine> = {}): TemplateLine => ({ key: `t${(seq += 1)}`, itemId: null, itemLabel: '', description: '', qty: '1', supplyBy: 'us', ...extra });

/** Template lines have no rates: rates are filled from Materials and Work rates when loaded into a quotation. */
function TemplateLines({ lines, onChange, type }: { lines: TemplateLine[]; onChange: (lines: TemplateLine[]) => void; type: 'service' | 'material' }) {
  const labour = type === 'service';
  const update = (key: string, patch: Partial<TemplateLine>) => onChange(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  return (
    <div>
      <ul className="divide-y divide-line border-t border-line">
        {lines.map((l, i) => (
          <li key={l.key} className="grid gap-2 px-5 py-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_90px_auto_36px] sm:items-center">
            <ItemPicker type={type} size="sm" value={l.itemId} selectedLabel={l.itemLabel} onChange={(id, it) => update(l.key, { itemId: id, itemLabel: it?.name ?? '' })} placeholder={labour ? 'Pick work (optional)' : 'Pick product'} />
            {labour ? (
              <Input size="sm" aria-label="Description" placeholder="Description" value={l.description} onChange={(e) => update(l.key, { description: e.target.value })} />
            ) : (
              <span className="hidden sm:block" />
            )}
            <Input size="sm" aria-label="Quantity" inputMode="decimal" value={l.qty} onChange={(e) => update(l.key, { qty: e.target.value.replace(/[^\d.]/g, '') })} />
            {labour ? (
              <span />
            ) : (
              <div className="flex gap-1 text-xs">
                {(
                  [
                    ['us', 'We supply'],
                    ['customer', 'Customer'],
                  ] as [SupplyBy, string][]
                ).map(([v, lab]) => (
                  <button key={v} type="button" aria-pressed={l.supplyBy === v} onClick={() => update(l.key, { supplyBy: v })} className={cn('rounded-md border px-2 py-1 font-medium', l.supplyBy === v ? 'border-brand-600 bg-brand-50 text-brand-700' : 'border-line text-ink-500')}>
                    {lab}
                  </button>
                ))}
              </div>
            )}
            <IconButton icon="trash" size="sm" variant="danger" label={`Remove line ${i + 1}`} onClick={() => onChange(lines.filter((x) => x.key !== l.key))} />
          </li>
        ))}
      </ul>
      <div className="border-t border-line px-5 py-3">
        <Button variant="ghost" size="sm" leftIcon="plus" className="-ml-2 text-brand-600" onClick={() => onChange([...lines, row()])}>{labour ? 'Add work' : 'Add material'}</Button>
      </div>
    </div>
  );
}

export default function TemplateForm() {
  const { id = '' } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const toast = useToast();
  const { busy, run } = useAction(toast);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [labour, setLabour] = useState([row()]);
  const [materials, setMaterials] = useState<TemplateLine[]>([]);
  const { form, setForm, set, errors, setErrors } = useForm({ name: '', description: '', isActive: true });
  const { data: t, loading, error, reload } = useFetch(() => (id ? templateService.expand(id) : Promise.resolve(null)), [id]);
  const { data: raw } = useFetch(() => (editing ? templateService.get(id) : Promise.resolve(null)), [id]);
  useCrumb(`/templates/${id}`, raw?.name);

  useEffect(() => {
    if (!t || !raw) return;
    setForm({ name: raw.name, description: raw.description ?? '', isActive: raw.isActive !== false });
    setLabour(t.labourItems.map((l, i) => row({ itemId: l.itemId ?? null, itemLabel: l.itemId ? l.description : '', description: raw.labourItems[i]?.description ?? '', qty: String(l.qty) })));
    setMaterials(t.materialItems.map((m) => row({ itemId: m.itemId ?? null, itemLabel: m.name, qty: String(m.qty), supplyBy: m.supplyBy ?? 'us' })));
  }, [t, raw, setForm]);

  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (editing && (loading || !raw) && !t) return <PageLoader />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const errs: FormErrors = {};
    if (!form.name.trim()) errs.name = 'Enter a template name';
    const bad = labour.find((l) => !l.itemId && !l.description.trim()) || [...labour, ...materials].find((l) => !(Number(l.qty) > 0)) || materials.find((m) => !m.itemId);
    if (bad) errs.lines = 'Every line needs an item (or a description for work) and a quantity';
    setErrors(errs);
    if (Object.keys(errs).length) return toast.error(errs.lines || 'Please fix the highlighted fields');
    const body: TemplateInput = {
      name: form.name.trim(),
      description: trimOrUndef(form.description),
      isActive: form.isActive,
      labourItems: labour.map((l) => ({ ...(l.itemId ? { itemId: l.itemId } : {}), ...(l.description.trim() ? { description: l.description.trim() } : {}), qty: Number(l.qty) })),
      materialItems: materials.map((m) => ({ itemId: m.itemId as string, qty: Number(m.qty), supplyBy: m.supplyBy })),
    };
    setSaving(true);
    try {
      id ? await templateService.update(id, body) : await templateService.create(body);
      toast.success('Template saved');
      navigate('/templates', { replace: true });
    } catch (err) {
      setErrors(serverFieldErrors(err));
      toast.error(err.message);
      setSaving(false);
    }
  };
  const remove = async () => {
    if (await run('delete', () => templateService.remove(id), 'Template deleted')) navigate('/templates', { replace: true });
    setConfirmDelete(false);
  };

  const actions = (
    <>
      {editing && <Button variant="dangerText" onClick={() => setConfirmDelete(true)}>Delete</Button>}
      <Button variant="outline" onClick={() => navigate('/templates')} disabled={saving}>Cancel</Button>
      <Button type="submit" form="template-form" leftIcon="check" loading={saving}>Save template</Button>
    </>
  );

  return (
    <>
      <PageHeader title={editing ? `Edit ${raw?.name ?? 'template'}` : 'New quote template'} subtitle="Rates come from Work rates and Materials when the template is loaded into a quotation." actions={actions} />
      <form id="template-form" onSubmit={submit} noValidate className="space-y-5">
        <FormSection title="Template">
          <Input label="Name" required autoFocus value={form.name} onChange={set('name')} error={errors.name} placeholder="e.g. 2BHK new wiring" />
          <Textarea label="Description" rows={1} value={form.description} onChange={set('description')} maxLength={1000} />
        </FormSection>
        <TableSection title="Fitting and labour">
          <TemplateLines lines={labour} onChange={setLabour} type="service" />
        </TableSection>
        <TableSection title="Material list">
          <TemplateLines lines={materials} onChange={setMaterials} type="material" />
        </TableSection>
        {editing && (
          <Card className="p-5">
            <Switch label="Show in quotations" description="Hidden templates can't be loaded into new quotations" checked={form.isActive} onChange={set('isActive')} />
          </Card>
        )}
        <div className="flex justify-end gap-2">{actions}</div>
      </form>
      <ConfirmDialog open={confirmDelete} onClose={() => setConfirmDelete(false)} onConfirm={remove} loading={busy === 'delete'} danger title="Delete this template?" message="Quotations already made from it are not affected." confirmLabel="Delete template" />
    </>
  );
}
